/* eslint-disable max-lines */
/* eslint-disable max-lines-per-function */
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { createStore } from 'zustand';
import { transformMitarbeiterStationenToArbeitsbereichStationen } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/utils/transformMitarbeiterStationenToArbeitsbereichStationen';
import { Beruf } from '@/domain/mitarbeiter/features/rotationsplan/entities/Beruf';
import { Abschnitt as RotationsplanAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt';
import {
  RotationsplanFeatureProps,
  RotationsplanFeatureState,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/types';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { filterRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/filterRotationsplaene';
import { filterRotationsplaeneByArbeitsbereiche } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/filterRotationsplaeneByArbeitsbereiche';
import {
  applyDefaultFilterAndSortingForRotationsplaene,
  applyDefaultFilterAndSortingForRotationsplaeneByArbeitsbereiche,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/applyDefaultFilterAndSorting';
import {
  sortRotationsplaene,
  sortRotationsplaeneByArbeitsbereich,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/sortRotationsplaene';
import { persist } from 'zustand/middleware';

export type RotationsplanFeatureStore = ReturnType<
  typeof createRotationsplanFeatureStore
>;

export const createRotationsplanFeatureStore = (
  rotationsplaene: Rotationsplan[],
  berufe: Beruf[],
  standorte: Standort[],
  currentStandort: Standort,
  year: string,
) => {
  let storedState:
    | { state: { ui: RotationsplanFeatureProps['ui'] } }
    | undefined;

  try {
    const raw = localStorage.getItem('rotationsplan-storage');
    storedState = raw
      ? (JSON.parse(raw) satisfies {
          state: { ui: RotationsplanFeatureProps['ui'] };
        })
      : undefined;
  } catch {
    storedState = undefined;
  }

  const defaultProps: RotationsplanFeatureProps = {
    data: {
      planung: {
        rotationsplaene: [],
        rotationsplaeneByArbeitsbereiche: [],
      },
      uebersicht: { rotationsplaene: [] },
      abschnitte: [],
      berufe: [],
      standorte: [],
    },
    ui: storedState?.state.ui ?? {
      year,
      kalender: {
        isScrolling: false,
        view: 'byArbeitsbereiche',
        istSortierungAufsteigend: true,
        currentStartOfWeek: dayjs()
          .year(parseInt(year))
          .startOf('isoWeek')
          .toISOString(),
        currentEndOfWeek: dayjs()
          .year(parseInt(year))
          .endOf('isoWeek')
          .toISOString(),
        selectedFilterCount: 0,
        filter: {
          berufe: [],
          standorte: [currentStandort],
          abschnitte: [],
        },
      },
      uebersicht: {
        filter: {
          abschnitte: [],
          berufe: [],
          status: ['geplant', 'aktiv'],
          standorte: [currentStandort],
        },
        istSortierungAufsteigend: true,
      },
    },
  };

  const rotationsplaeneByArbeitsbereiche =
    transformMitarbeiterStationenToArbeitsbereichStationen(rotationsplaene);

  const abschnitte = rotationsplaene
    .flatMap((plan) => plan.abschnitte)
    .reduce((abschnitte, current): RotationsplanAbschnitt[] => {
      const names = abschnitte.map((a) => a.position + '. ' + a.name);
      if (!names.includes(current.position + '. ' + current.name)) {
        abschnitte.push(current);
      }
      return abschnitte;
    }, [])
    .sort((a, b) => {
      // Lehrjahr kommt immer zuerst
      if (a.name === 'Lehrjahr' && b.name !== 'Lehrjahr') return -1;
      if (a.name !== 'Lehrjahr' && b.name === 'Lehrjahr') return 1;

      // dann nach Position vergleichen
      if (a.position !== b.position) {
        return a.position - b.position;
      }

      // am Ende nach Name vergleichen
      return a.name.localeCompare(b.name);
    })
    .map((abschnitt) => ({
      name: `${abschnitt.position}. ${abschnitt.name}`,
      value: abschnitt.id,
    }));

  return createStore<RotationsplanFeatureState>()(
    persist(
      (set) => ({
        data: {
          planung: {
            rotationsplaene: applyDefaultFilterAndSortingForRotationsplaene(
              rotationsplaene,
              'standorte',
              defaultProps.ui.kalender.filter.standorte,
              defaultProps.ui.kalender.filter,
              defaultProps.ui.kalender.istSortierungAufsteigend,
            ),
            rotationsplaeneByArbeitsbereiche:
              applyDefaultFilterAndSortingForRotationsplaeneByArbeitsbereiche(
                rotationsplaeneByArbeitsbereiche,
                defaultProps,
              ),
          },
          uebersicht: {
            rotationsplaene: applyDefaultFilterAndSortingForRotationsplaene(
              rotationsplaene,
              'standorte',
              defaultProps.ui.uebersicht.filter.standorte,
              defaultProps.ui.uebersicht.filter,
              defaultProps.ui.uebersicht.istSortierungAufsteigend,
            ),
          },
          abschnitte,
          berufe,
          standorte,
        },
        ui: {
          ...defaultProps.ui,
        },
        setKalenderView: (view) =>
          set((state) => ({
            ui: { ...state.ui, kalender: { ...state.ui.kalender, view } },
          })),
        setKalenderSortierung: (istSortierungAufsteigend) =>
          set((state) => ({
            data: {
              ...state.data,
              planung: {
                rotationsplaene: sortRotationsplaene(
                  state.data.planung.rotationsplaene,
                  istSortierungAufsteigend,
                ),
                rotationsplaeneByArbeitsbereiche:
                  sortRotationsplaeneByArbeitsbereich(
                    state.data.planung.rotationsplaeneByArbeitsbereiche,
                    istSortierungAufsteigend,
                  ),
              },
            },
            ui: {
              ...state.ui,
              kalender: { ...state.ui.kalender, istSortierungAufsteigend },
            },
          })),
        setKalenderScrolling: (isScrolling) =>
          set((state) => ({
            ui: {
              ...state.ui,
              kalender: { ...state.ui.kalender, isScrolling },
            },
          })),
        setCurrentStartOfWeek: (currentStartOfWeek) =>
          set((state) => ({
            ui: {
              ...state.ui,
              kalender: { ...state.ui.kalender, currentStartOfWeek },
            },
          })),
        setCurrentEndOfWeek: (currentEndOfWeek) =>
          set((state) => ({
            ui: {
              ...state.ui,
              kalender: { ...state.ui.kalender, currentEndOfWeek },
            },
          })),
        setKalenderBerufeFilter: (selectedBerufe) =>
          set((state) => ({
            data: {
              ...state.data,
              planung: {
                rotationsplaeneByArbeitsbereiche:
                  filterRotationsplaeneByArbeitsbereiche(
                    'berufe',
                    selectedBerufe,
                    state.ui.kalender.filter,
                    rotationsplaeneByArbeitsbereiche,
                  ),
                rotationsplaene: filterRotationsplaene(
                  'berufe',
                  selectedBerufe,
                  state.ui.kalender.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              kalender: {
                ...state.ui.kalender,
                filter: {
                  ...state.ui.kalender.filter,
                  berufe: [...selectedBerufe],
                },
              },
            },
          })),
        setKalenderGliederungFilter: (selectedAbschnitte) => {
          return set((state) => ({
            data: {
              ...state.data,
              planung: {
                rotationsplaeneByArbeitsbereiche:
                  filterRotationsplaeneByArbeitsbereiche(
                    'abschnitte',
                    selectedAbschnitte,
                    state.ui.kalender.filter,
                    rotationsplaeneByArbeitsbereiche,
                  ),
                rotationsplaene: filterRotationsplaene(
                  'abschnitte',
                  selectedAbschnitte,
                  state.ui.kalender.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              kalender: {
                ...state.ui.kalender,
                filter: {
                  ...state.ui.kalender.filter,
                  abschnitte: [...selectedAbschnitte],
                },
              },
            },
          }));
        },
        setKalenderStandortFilter: (selectedStandorte) => {
          return set((state) => ({
            data: {
              ...state.data,
              planung: {
                rotationsplaeneByArbeitsbereiche:
                  filterRotationsplaeneByArbeitsbereiche(
                    'standorte',
                    selectedStandorte,
                    state.ui.kalender.filter,
                    rotationsplaeneByArbeitsbereiche,
                  ),
                rotationsplaene: filterRotationsplaene(
                  'standorte',
                  selectedStandorte,
                  state.ui.kalender.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              kalender: {
                ...state.ui.kalender,
                filter: {
                  ...state.ui.kalender.filter,
                  standorte: [...selectedStandorte],
                },
              },
            },
          }));
        },
        setUebersichtBerufeFilter: (selectedBerufe) =>
          set((state) => ({
            data: {
              ...state.data,
              uebersicht: {
                rotationsplaene: filterRotationsplaene(
                  'berufe',
                  selectedBerufe,
                  state.ui.uebersicht.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              uebersicht: {
                ...state.ui.uebersicht,
                filter: {
                  ...state.ui.uebersicht.filter,
                  berufe: [...selectedBerufe],
                },
              },
            },
          })),
        setUebersichtGliederungFilter: (selectedAbschnitte) => {
          return set((state) => ({
            data: {
              ...state.data,
              uebersicht: {
                rotationsplaene: filterRotationsplaene(
                  'abschnitte',
                  selectedAbschnitte,
                  state.ui.uebersicht.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              uebersicht: {
                ...state.ui.uebersicht,
                filter: {
                  ...state.ui.uebersicht.filter,
                  abschnitte: [...selectedAbschnitte],
                },
              },
            },
          }));
        },
        setUebersichtStatusFilter: (selectedStatus) => {
          return set((state) => ({
            data: {
              ...state.data,
              uebersicht: {
                rotationsplaene: filterRotationsplaene(
                  'status',
                  selectedStatus,
                  state.ui.uebersicht.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              uebersicht: {
                ...state.ui.uebersicht,
                filter: {
                  ...state.ui.uebersicht.filter,
                  status: [...selectedStatus],
                },
              },
            },
          }));
        },
        setYear: (year) => {
          set((state) => {
            const currentWeek = dayjs(
              state.ui.kalender.currentStartOfWeek,
            ).isoWeek();
            const currentStartOfWeek = dayjs()
              .year(Number(year))
              .isoWeek(currentWeek)
              .startOf('isoWeek');
            const currentEndOfWeek = currentStartOfWeek.endOf('isoWeek');

            return {
              data: {
                ...state.data,
              },
              ui: {
                ...state.ui,
                year,
                kalender: {
                  ...state.ui.kalender,
                  currentStartOfWeek: currentStartOfWeek.toISOString(),
                  currentEndOfWeek: currentEndOfWeek.toISOString(),
                },
              },
            };
          });
        },
        setUebersichtStandortFilter: (selectedStandorte) => {
          return set((state) => ({
            data: {
              ...state.data,
              uebersicht: {
                rotationsplaene: filterRotationsplaene(
                  'standorte',
                  selectedStandorte,
                  state.ui.uebersicht.filter,
                  rotationsplaene,
                ),
              },
            },
            ui: {
              ...state.ui,
              uebersicht: {
                ...state.ui.uebersicht,
                filter: {
                  ...state.ui.uebersicht.filter,
                  standorte: [...selectedStandorte],
                },
              },
            },
          }));
        },
        setUebersichtSortierung: (istSortierungAufsteigend) =>
          set((state) => ({
            data: {
              ...state.data,
              uebersicht: {
                rotationsplaene: sortRotationsplaene(
                  state.data.uebersicht.rotationsplaene,
                  istSortierungAufsteigend,
                ),
              },
            },
            ui: {
              ...state.ui,
              uebersicht: {
                ...state.ui.uebersicht,
                istSortierungAufsteigend,
              },
            },
          })),
      }),
      {
        name: 'rotationsplan-storage',
        partialize: (state) => ({
          ui: state.ui,
        }),
      },
    ),
  );
};
